/**
 * The only place fetch is called. Everything goes through /api on the same
 * origin — nginx proxies it — so there is no base URL to configure and no CORS.
 */

export class ApiError extends Error {
  readonly status: number
  /** The server's machine-readable code, e.g. 'mfa_required'. */
  readonly code: string | undefined

  constructor(status: number, message: string, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

/**
 * `background`: a request the page makes on its own (a screen refreshing
 * itself), not one a person caused. The server does not count it as activity
 * toward the 15-minute idle sign-out (CCR §19012, migration 0096) — otherwise
 * a dashboard left open on an empty desk would keep its session alive forever.
 */
export async function apiGet<T>(path: string, options: { background?: boolean } = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    // Sessions are server-side with an httpOnly cookie (§6c), so credentials
    // must ride along once auth exists. Setting it now costs nothing.
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      ...(options.background ? { 'x-blocwork-background': '1' } : {}),
    },
  })

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null
    throw new ApiError(response.status, body?.message ?? `Request failed (${response.status})`, (body as { error?: string } | null)?.error)
  }

  return (await response.json()) as T
}

export async function apiPost<T>(path: string, body: unknown = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const parsed = (await response.json().catch(() => null)) as
      | { error?: string; message?: string }
      | null
    // Carry the server's error code, not just the status: the auth flow
    // distinguishes mfa_required from unauthenticated, and both are 401.
    throw new ApiError(
      response.status,
      parsed?.message ?? parsed?.error ?? `Request failed (${response.status})`,
      parsed?.error,
    )
  }

  return (await response.json()) as T
}

export async function apiPut<T>(path: string, body: unknown = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const parsed = (await response.json().catch(() => null)) as
      | { error?: string; message?: string }
      | null
    throw new ApiError(
      response.status,
      parsed?.message ?? parsed?.error ?? `Request failed (${response.status})`,
      parsed?.error,
    )
  }

  return (await response.json()) as T
}

/** What the browser is allowed to do right now. Mirrors GET /api/auth/session. */
export type SessionState =
  | { state: 'anonymous' }
  | { state: 'mfa_required'; email: string; enrollment_required: boolean }
  | {
      state: 'authenticated'
      user_id: string
      email: string
      display_name: string | null
      grants: { role: string; scope_type: string; scope_id: string | null }[]
      /** Older than 365 days: the only thing to do is change it (0096). */
      password_expired?: true
      /** DEV SCAFFOLDING — this session skipped its second factor (§6c). */
      mfa_bypassed?: true
    }

export interface EnrollResponse {
  factor_id: string
  secret: string
  otpauth_uri: string
}

export interface Snapshot {
  extract_date: string
  coverage: string
  status: string
  archive_path: string
  loaded_at: string | null
}

export interface PlatformSummary {
  persons: number
  voters_current: number
  voters_absent: number
  households: number
  addresses: number
  elections: number
  vote_history_rows: number
  with_phone: number
  nonstandard_address: number
  snapshots: Snapshot[]
  latest_extract_date: string | null
  generated_at: string
}
